import { useCallback, useState } from "react";
import { UploadCloud, ImageIcon, Loader2, CheckCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAppStore } from "../store/useAppStore";

interface UploadPageProps {
  onComplete?: () => void;
}

export function UploadPage({ onComplete }: UploadPageProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploaded, setUploaded] = useState(false);
  const { setCurrentDesign, setIsUploading, isUploading } = useAppStore();

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const file = files[0];

    // Validate
    if (!file.type.match(/image\/(png|jpeg)/)) {
      alert("只支持 PNG 或 JPG 格式");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert("文件大小不能超過 20MB");
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      // 1. Get presigned URLs
      const uploadInfo = await api.getUploadUrls(file);
      setUploadProgress(30);

      // 2. Upload original to R2
      await uploadBlob(uploadInfo.uploadUrls.original, file, file.type);
      setUploadProgress(60);

      // 3. Generate thumbnail (client-side) and upload
      const thumbnailBlob = await generateThumbnail(file);
      await uploadBlob(uploadInfo.uploadUrls.thumbnail, thumbnailBlob, "image/webp");
      setUploadProgress(80);

      // 4. Confirm upload
      const img = await loadImage(file);
      const design = await api.confirmUpload({
        designId: uploadInfo.designId,
        name: file.name.replace(/\.[^/.]+$/, ""),
        width: img.width,
        height: img.height,
        format: file.type === "image/png" ? "png" : "jpg",
        hasTransparency: file.type === "image/png",
        originalUrl: uploadInfo.originalUrl,
        thumbnailUrl: uploadInfo.thumbnailUrl,
      });
      setUploadProgress(100);

      // 5. Set current design
      setCurrentDesign({
        id: design.id,
        userId: "",
        name: file.name,
        originalUrl: uploadInfo.originalUrl,
        thumbnailUrl: uploadInfo.thumbnailUrl,
        width: img.width,
        height: img.height,
        format: file.type === "image/png" ? "png" : "jpg",
        hasTransparency: file.type === "image/png",
        createdAt: new Date().toISOString(),
      });

      setUploaded(true);
      setTimeout(() => onComplete?.(), 1000);
    } catch (error) {
      console.error("Upload failed:", error);
      alert("上傳失敗，請重試");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  }, []);

  if (uploaded) {
    return (
      <div className="text-center py-16">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900">上傳成功！</h3>
        <p className="text-gray-500 mt-2">正在跳轉到編輯器...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">上傳你的設計</h2>
        <p className="text-gray-500">
          支持 PNG、JPG 格式，最大 20MB，建議分辨率 ≥1000×1000
        </p>
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-xl p-12 text-center cursor-pointer
          transition-colors duration-200
          ${isDragging
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-gray-400 bg-white"
          }
        `}
      >
        <input
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          id="file-upload"
          onChange={handleFileSelect}
          disabled={isUploading}
        />
        <label htmlFor="file-upload" className="cursor-pointer block">
          {isUploading ? (
            <div className="space-y-4">
              <Loader2 className="animate-spin h-12 w-12 text-blue-500 mx-auto" />
              <div className="max-w-xs mx-auto">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">{uploadProgress}%</p>
              </div>
            </div>
          ) : isDragging ? (
            <UploadCloud className="mx-auto h-12 w-12 text-blue-500 mb-4" />
          ) : (
            <ImageIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          )}

          {!isUploading && (
            <>
              <p className="text-lg font-medium text-gray-700">
                {isDragging ? "放開以上傳" : "拖放設計圖到這裡，或點擊選擇"}
              </p>
              <p className="mt-2 text-sm text-gray-500">
                PNG / JPG · 最大 20MB
              </p>
            </>
          )}
        </label>
      </div>

      <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-medium">💡 小貼士</p>
        <ul className="mt-2 space-y-1 list-disc list-inside">
          <li>使用透明背景的 PNG 可以獲得最佳效果</li>
          <li>設計圖分辨率建議 ≥1000×1000 像素</li>
          <li>上傳後可以在編輯器中調整設計位置和大小</li>
        </ul>
      </div>
    </div>
  );
}

// Helper: load image dimensions
function loadImage(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// Helper: generate thumbnail
async function generateThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 400;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d")!;

      // Center crop to square
      const minDim = Math.min(img.width, img.height);
      const sx = (img.width - minDim) / 2;
      const sy = (img.height - minDim) / 2;

      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to generate thumbnail"));
        },
        "image/webp",
        0.85
      );
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function uploadBlob(url: string, body: Blob, contentType: string): Promise<void> {
  const response = await fetch(url, {
    method: "PUT",
    body,
    headers: { "Content-Type": contentType },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`);
  }
}
